import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import { fetchAIParagraphs } from './api';
import { resetAllDecorations } from './decorations';
import { createCommand, getPluginState } from './plugin-factory';
import { ACTIONS } from './states';
import { getDiffObjectsForBlocks } from './utils';
export const insertSuggestion = (from, to, tr) => createCommand(state => {
  const pluginState = getPluginState(state);
  const {
    decorationSet,
    allDiffObjects: currentDiffObjects
  } = pluginState;
  if (!currentDiffObjects) {
    return false;
  }
  const allDiffObjects = currentDiffObjects.filter(({
    from: _from,
    to: _to
  }) => from !== _from && to !== _to);
  return {
    type: ACTIONS.UPDATE_PLUGIN_STATE,
    data: {
      allDiffObjects,
      // Upon inserting a suggestion we need to ensure that that the insertion widget is removed.
      decorationSet: resetAllDecorations({
        decorationSet,
        tr: tr || state.tr,
        pluginState: {
          ...pluginState,
          allDiffObjects
        }
      })
    }
  };
}, (originalTr, state) => {
  const {
    schema
  } = state;
  const {
    allDiffObjects
  } = getPluginState(state);
  const transaction = tr || originalTr;
  const {
    selection
  } = transaction;
  if (allDiffObjects && selection.from === selection.to) {
    const selectedDiff = allDiffObjects === null || allDiffObjects === void 0 ? void 0 : allDiffObjects.find(({
      from,
      to
    }) => selection.from <= to && selection.from >= from);
    if (selectedDiff) {
      var _resolvedPos$nodeAfte;
      /**
       * mark transaction as suggestionAccepted, so that we can ignore update
       * 	while updating proactiveAIBlocks.
       */
      transaction.setMeta('suggestionAccepted', true);
      const {
        from,
        to,
        text
      } = selectedDiff;

      /**
       * Take the marks from the 'from' position
       * (beginning of the word to be replaced)
       *
       * This is to ensure that the new text has the same marks as the old text.
       * To be more pragmatic, we only look at 'from' position in case the
       * word has different mark combinations in each letter.
       */
      const resolvedPos = state.doc.resolve(from);
      const marks = ((_resolvedPos$nodeAfte = resolvedPos.nodeAfter) === null || _resolvedPos$nodeAfte === void 0 ? void 0 : _resolvedPos$nodeAfte.marks) || [];
      return transaction.replaceWith(from, to, schema.text(text, marks));
    }
  }
  return transaction;
});
export const removeSuggestion = (from, to, originalText, tr) => createCommand(state => {
  const pluginState = getPluginState(state);
  const {
    decorationSet,
    allDiffObjects: currentDiffObjects
  } = pluginState;
  if (!currentDiffObjects) {
    return false;
  }
  const allDiffObjects = currentDiffObjects.filter(({
    from: _from,
    to: _to,
    originalText: _text
  }) => {
    return originalText !== _text;
  });
  return {
    type: ACTIONS.UPDATE_PLUGIN_STATE,
    data: {
      allDiffObjects,
      newDismissedWord: originalText,
      decorationSet: resetAllDecorations({
        decorationSet,
        tr: tr || state.tr,
        pluginState: {
          ...pluginState,
          allDiffObjects
        }
      })
    }
  };
});
export const toggleSpellingAndGrammar = () => createCommand({
  type: ACTIONS.TOGGLE_SPELLING_AND_GRAMMAR_ENABLED
});
export const updateBlocksWithDiffObjects = async (view, blocksNeedingSpellAndGrammerCheck, locale) => {
  const state = view.state;
  const {
    proactiveAIApiUrl
  } = getPluginState(state);
  const suggestionGenerator = fetchAIParagraphs(proactiveAIApiUrl, locale)(blocksNeedingSpellAndGrammerCheck);
  if (!suggestionGenerator) {
    return;
  }
  for await (const suggestionState of suggestionGenerator) {
    switch (suggestionState.state) {
      case 'parsed':
        {
          const suggestion = suggestionState.suggestion;
          const block = blocksNeedingSpellAndGrammerCheck.find(block => block.id === suggestion.original.id);
          if (!block) {
            break;
          }

          /**
           * TODO EDF-848
           * This should be refined since we now process suggestions individually as it streams
           */
          const blocksDiffObjectsByBlockId = getDiffObjectsForBlocks([{
            block,
            suggestion
          }]);
          createCommand(() => {
            return {
              type: ACTIONS.UPDATE_BLOCKS_DIFF_OBJECTS,
              data: {
                blocksDiffObjectsByBlockId
              }
            };
          }, tr => {
            return tr.setMeta('addToHistory', false);
          })(view.state, view.dispatch);

          /**
           * TODO EDF-848
           * This currently recalculates all diff objects and decorations in the document
           * This should be refined to only update the diff objects and decorations for the block that has been updated
           */
          updateAllDiffObjectsFromBlocks(view);
          break;
        }
      case 'done':
        {
          return;
        }
    }
  }
};
export const updateAllDiffObjectsFromBlocks = view => {
  const {
    proactiveAIBlocks,
    dismissedWords
  } = getPluginState(view.state);
  if (proactiveAIBlocks && proactiveAIBlocks.length > 0) {
    /**
     * When block is affected by transaction, we create new block out of it.
     * New block won't have diffObjects in it.
     * Then when we call S+G API, we will get new diffObjects stored in blocks.
     * Now after having new diffObjects in blocks, then we need to
     *  create allDiffObjects and decorations from it.
     */
    let allDiffObjects = [];
    proactiveAIBlocks.forEach(block => {
      if (block.diffObjects) {
        allDiffObjects = allDiffObjects.concat(...block.diffObjects);
      }
    });
    allDiffObjects = allDiffObjects.filter(({
      originalText
    }) => !dismissedWords.has(originalText));
    const command = createCommand(state => {
      const pluginState = getPluginState(state);
      const {
        decorationSet
      } = pluginState;
      return {
        type: ACTIONS.UPDATE_PLUGIN_STATE,
        data: {
          decorationSet: resetAllDecorations({
            decorationSet,
            tr: state.tr,
            pluginState: {
              ...pluginState,
              allDiffObjects
            }
          }),
          allDiffObjects
        }
      };
    }, (tr, state) => {
      return tr.setMeta('addToHistory', false);
    });
    command(view.state, view.dispatch);
  }
};
const isSelectionInBlock = (from, to, block) => {
  return block.from <= from && block.to >= from || block.from <= to && block.to >= to;
};
const getBlocksNeedingSpellAndGrammerCheck = state => {
  const {
    proactiveAIBlocks
  } = getPluginState(state);
  if (!proactiveAIBlocks || !proactiveAIBlocks.length) {
    return [];
  }
  return proactiveAIBlocks === null || proactiveAIBlocks === void 0 ? void 0 : proactiveAIBlocks.filter(block => !!block.needSpellingAndGrammarCheck);
};
const getBlocksEligibleForSGPrompt = (state, currentBlocks) => {
  const blocksNeedingSpellAndGrammerCheck = getBlocksNeedingSpellAndGrammerCheck(state);
  const {
    from,
    to
  } = state.selection;
  return blocksNeedingSpellAndGrammerCheck.filter(block => {
    const selectionInBlock = isSelectionInBlock(from, to, block);
    return currentBlocks ? selectionInBlock : !selectionInBlock;
  });
};
/**
 * Wait Y (Y > X) seconds before triggering S+G prompt for current blocks.
 * That means,
 *  Author starts updating block and keeps updating for sometime.
 *  Then Author pauses for Y (Y > X) seconds then trigger S+G prompt.
 */
export const triggerSpellingAndGrammarCheckForCurrentBlocks = async (view, locale) => {
  /**
   * If selection is overlap with block and it's been more than Y seconds
   *  since it was last updated then send it for S+G prompt.
   * Ignore non current blocks, as they will be handled separately.
   */
  const blocksEligibleForSGPrompt = getBlocksEligibleForSGPrompt(view.state, true);
  if (blocksEligibleForSGPrompt.length) {
    await updateBlocksWithDiffObjects(view, blocksEligibleForSGPrompt, locale);
  }
};

/**
 * Wait X seconds before triggering S+G prompt for non current blocks.
 * That means,
 *  Author starts updating block, till cursor (or selection) is in the block,
 *    and S+G prompt hasn't been run since last update then,
 *    trigger S+G prompts after X seconds.
 */
export const triggerSpellingAndGrammarCheckForNonCurrentBlocks = async (view, locale) => {
  const blocksEligibleForSGPrompt = getBlocksEligibleForSGPrompt(view.state, false);
  if (blocksEligibleForSGPrompt.length) {
    await updateBlocksWithDiffObjects(view, blocksEligibleForSGPrompt, locale);
  }
};
export const createTriggerSpellingAndGrammerCheck = timings => {
  const triggerSpellingAndGrammarCheckForNonCurrentBlocksThrottled = throttle(triggerSpellingAndGrammarCheckForNonCurrentBlocks, timings.nonCurrentChunks);
  const triggerSpellingAndGrammarCheckForCurrentBlocksDebounced = debounce(triggerSpellingAndGrammarCheckForCurrentBlocks, timings.currentChunks, {
    maxWait: timings.currentChunksMaxWait
  });
  const triggerSpellingAndGrammerCheck = (view, locale) => {
    triggerSpellingAndGrammarCheckForNonCurrentBlocksThrottled(view, locale);
    triggerSpellingAndGrammarCheckForCurrentBlocksDebounced(view, locale);
  };
  const cancelDebouncedAndThrottledSpellingAndGrammerCheck = () => {
    triggerSpellingAndGrammarCheckForNonCurrentBlocksThrottled.cancel();
    triggerSpellingAndGrammarCheckForCurrentBlocksDebounced.cancel();
  };
  return {
    triggerSpellingAndGrammerCheck,
    cancelDebouncedAndThrottledSpellingAndGrammerCheck
  };
};